/**
 * Add-to-BoM actions on product pages and in the cart.
 *
 * - `<bom-add-button>` for standard products (reads current variant + qty)
 * - `<bom-add-cart-button>` for cart / cart-drawer (adds every current line)
 * - Programmatic `addConfiguredToBom(...)` for the C3 configurator
 */
import { bomStore, BomLineKind, BomLineStatus, BOM_CHANGE_EVENT } from '@theme/bom-store';
import { StandardEvents } from '@shopify/events';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

/** @type {WeakMap<HTMLButtonElement, number[]>} */
const bomAddedTimeouts = new WeakMap();

/**
 * Opens the BoM side menu.
 */
export function openBomDrawer() {
  const drawer = document.getElementById('bom-drawer');
  if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
    drawer.open();
  }
}

/**
 * Leaves the button labeled "Added to BoM" after the checkmark animation.
 * A later click opens the BoM menu instead of adding again.
 * @param {HTMLButtonElement|null} button
 * @param {string} addedLabel
 */
export function markBomButtonAdded(button, addedLabel) {
  if (!button) return;
  const label = button.querySelector('[data-bom-label]');
  if (label instanceof HTMLElement && !button.dataset.bomAddLabel) {
    button.dataset.bomAddLabel = label.textContent.trim();
  }
  if (label instanceof HTMLElement && addedLabel) label.textContent = addedLabel;
  button.dataset.bomState = 'added';
}

/**
 * Restores the original "Add to BoM" label.
 * @param {HTMLButtonElement|null} button
 */
export function resetBomButton(button) {
  if (!button || button.dataset.bomState !== 'added') return;
  const label = button.querySelector('[data-bom-label]');
  if (label instanceof HTMLElement && button.dataset.bomAddLabel) {
    label.textContent = button.dataset.bomAddLabel;
  }
  delete button.dataset.bomState;
}

/**
 * Plays the add-to-cart checkmark burst on an Add to BoM button.
 * `host` also receives `.bom-add--added` when the success class lives on a wrapper.
 * @param {HTMLButtonElement|null} button
 * @param {HTMLElement} [host]
 */
export async function playBomAddedAnimation(button, host = button) {
  if (!button) return;

  const pending = bomAddedTimeouts.get(button) ?? [];
  pending.forEach((timeoutId) => clearTimeout(timeoutId));
  /** @type {number[]} */
  const timeouts = [];
  bomAddedTimeouts.set(button, timeouts);

  if (button.dataset.added !== 'true') {
    button.dataset.added = 'true';
  }

  button.classList.add('bom-add--added');
  if (host && host !== button) host.classList.add('bom-add--added');

  await yieldToMainThread();
  // Don't wait forever if the checkmark transitions never start (offscreen cards).
  await Promise.race([
    onAnimationEnd(button),
    new Promise((resolve) => window.setTimeout(resolve, 1600)),
  ]);

  const timeoutId = window.setTimeout(() => {
    button.classList.remove('bom-add--added');
    button.removeAttribute('data-added');
    if (host && host !== button) host.classList.remove('bom-add--added');
    const index = timeouts.indexOf(timeoutId);
    if (index > -1) timeouts.splice(index, 1);
  }, 800);

  timeouts.push(timeoutId);
}

/**
 * @typedef {object} BomAddButtonRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} [status]
 */

/**
 * @extends {import('@theme/component').Component<BomAddButtonRefs>}
 */
class BomAddButton extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('[data-bom-add]') || this.querySelector('button');
    this.#status = this.querySelector('[data-bom-status]');
    this.#button?.addEventListener('click', this.#onClick);
    document.addEventListener(BOM_CHANGE_EVENT, this.#onBomChange);
    this.#productSelectTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#productSelectTarget?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    document.removeEventListener(BOM_CHANGE_EVENT, this.#onBomChange);
    this.#productSelectTarget?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /** @type {HTMLButtonElement|null} */
  #button = null;

  /** @type {HTMLElement|null} */
  #status = null;

  /** @type {Element|null} */
  #productSelectTarget = null;

  /**
   * Keeps the button in sync with variant picks: disabled whenever add-to-cart
   * is blocked (no matching variant or unavailable), enabled otherwise.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { newProduct, resource } = detail;
        if (newProduct) {
          this.dataset.productId = newProduct.id;
          if (newProduct.handle) this.dataset.productHandle = newProduct.handle;
          if (newProduct.title) this.dataset.productTitle = newProduct.title;
          if (newProduct.vendor) this.dataset.vendor = newProduct.vendor;
        }

        if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        this.#syncVariant(resource);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bom-add] Event promise rejected:', error);
      });
  };

  /**
   * @param {object|null} variant - The selected variant, or null when options don't resolve to one.
   */
  #syncVariant(variant) {
    const nextId = variant?.id != null ? String(variant.id) : '';
    if (nextId !== (this.dataset.variantId || '')) resetBomButton(this.#button);

    const available = variant != null && variant.available === true;
    if (this.#button) this.#button.disabled = !available;
    this.dataset.available = available ? 'true' : 'false';
    this.dataset.variantId = variant?.id ?? '';
    this.dataset.sku = variant?.sku || '';
    this.dataset.price = variant?.price != null ? String(variant.price) : '';
    if (!available) this.#setStatus('', false);
  }

  #onBomChange = () => {
    const button = this.#button;
    if (!button || button.dataset.bomState !== 'added') return;
    const variantId = Number(this.dataset.variantId || 0);
    if (!variantId) return;
    const present = bomStore.lines.some((line) => Number(line.variantId) === variantId);
    if (!present) resetBomButton(button);
  };

  #onClick = async (event) => {
    event.preventDefault();
    if (!this.#button || this.#button.disabled) return;

    if (this.#button.dataset.bomState === 'added') {
      openBomDrawer();
      return;
    }

    await bomStore.init();
    if (!bomStore.canEdit) {
      this.#setStatus(this.dataset.textReadOnly || 'This shared BoM is read-only.', true);
      return;
    }

    const line = this.#buildLine();
    if (!line) {
      this.#setStatus(this.dataset.textUnavailable || 'This product is unavailable.', true);
      return;
    }

    const added = bomStore.addLine(line);
    if (!added) {
      this.#setStatus(this.dataset.textError || 'Could not add to BoM.', true);
      return;
    }

    const addedLabel = this.dataset.textAdded || 'Added to BoM';
    this.#setStatus(addedLabel, false);
    markBomButtonAdded(this.#button, addedLabel);
    const button = this.#button;
    const fromPointer = event.detail > 0;
    void playBomAddedAnimation(button, this).then(() => {
      if (fromPointer && button && document.activeElement === button) button.blur();
    });
  };

  #buildLine() {
    const form = this.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]')
      || this.closest('form[data-type="add-to-cart-form"]')
      || document.querySelector('product-form-component form[data-type="add-to-cart-form"]');

    const variantInput = form?.querySelector('[name="id"]');
    const quantityInput = form?.querySelector('[name="quantity"]')
      || this.closest('product-form-component')?.querySelector('[name="quantity"]');

    const variantId = Number(variantInput?.value || this.dataset.variantId || 0);
    if (!variantId) return null;

    const quantity = Math.max(1, Math.floor(Number(quantityInput?.value || this.dataset.quantity || 1)));
    const available = this.dataset.available !== 'false';

    return {
      kind: BomLineKind.VARIANT,
      status: available ? BomLineStatus.FOUND : BomLineStatus.UNAVAILABLE,
      sku: this.dataset.sku || '',
      quantity,
      variantId,
      productId: this.dataset.productId ? Number(this.dataset.productId) : null,
      productHandle: this.dataset.productHandle || '',
      title: this.dataset.productTitle || '',
      vendor: this.dataset.vendor || '',
      price: this.dataset.price != null && this.dataset.price !== ''
        ? Number(this.dataset.price)
        : null,
      properties: null,
    };
  }

  #setStatus(message, isError) {
    if (!this.#status) return;
    this.#status.textContent = message;
    this.#status.hidden = !message;
    this.#status.setAttribute('data-tone', isError ? 'error' : 'success');
    // Success on a product card is the button animation. Keep the live text
    // out of the layout so the card does not grow.
    if (this.#status.classList.contains('pa-card-add__bom-status')) {
      this.#status.classList.toggle('visually-hidden', !isError);
    }
  }
}

/**
 * Add a validated C3 configuration to the active BoM.
 * @param {object} input
 * @param {string} input.catalogNumber
 * @param {string} input.cfg
 * @param {string} input.configuration
 * @param {number} input.unitPrice
 * @param {number} input.quantity
 * @param {number|string} input.variantId
 * @param {string} input.productHandle
 * @param {string} input.productTitle
 * @param {number} [input.productId]
 * @param {string} [input.vendor]
 * @returns {Promise<object|null>}
 */
export async function addConfiguredToBom(input) {
  await bomStore.init();
  if (!bomStore.canEdit) return null;

  const unitPrice = Number(input.unitPrice);
  return bomStore.addLine({
    kind: BomLineKind.CONFIGURED,
    status: BomLineStatus.FOUND,
    sku: input.catalogNumber,
    quantity: Math.max(1, Math.floor(Number(input.quantity) || 1)),
    variantId: Number(input.variantId) || null,
    productId: input.productId ?? null,
    productHandle: input.productHandle || '',
    title: `${input.productTitle || 'Configured product'} — ${input.catalogNumber}`,
    vendor: input.vendor || '',
    price: Number.isFinite(unitPrice) ? Math.round(unitPrice * 100) : null,
    properties: {
      _catalogNumber: input.catalogNumber,
      _cfg: input.cfg,
      _configuration: input.configuration,
      _unitPrice: Number.isFinite(unitPrice) ? unitPrice.toFixed(2) : '0.00',
    },
  });
}

if (!customElements.get('bom-add-button')) {
  customElements.define('bom-add-button', BomAddButton);
}

/**
 * Map a Shopify `/cart.js` line item to a BoM draft line.
 * Configured C3 lines keep `_cfg` / catalog / unit price instead of variant price.
 * @param {object} item
 * @returns {Partial<import('@theme/bom-store').BomLine>|null}
 */
export function lineFromCartItem(item) {
  if (!item) return null;

  const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
  const cfg = properties._cfg != null && properties._cfg !== '' ? String(properties._cfg) : '';
  const quantity = Math.max(1, Math.floor(Number(item.quantity) || 1));
  const variantId = Number(item.variant_id || item.id || 0) || null;
  const productId = Number(item.product_id || 0) || null;
  const productHandle = item.handle || '';
  const productTitle = item.product_title || item.title || '';

  if (cfg) {
    let configuration = properties._configuration || '';
    for (let i = 2; i <= 20; i++) {
      const chunk = properties[`_configuration${i}`];
      if (!chunk) break;
      configuration += chunk;
    }

    const catalogNumber = properties._catalogNumber || item.sku || '';
    const unitPrice = Number(properties._unitPrice);

    return {
      kind: BomLineKind.CONFIGURED,
      status: BomLineStatus.FOUND,
      sku: catalogNumber,
      quantity,
      variantId,
      productId,
      productHandle,
      title: `${productTitle || 'Configured product'} — ${catalogNumber}`,
      vendor: item.vendor || '',
      price: Number.isFinite(unitPrice) ? Math.round(unitPrice * 100) : (item.final_price ?? item.price ?? null),
      properties: {
        _catalogNumber: catalogNumber,
        _cfg: cfg,
        _configuration: configuration,
        _unitPrice: Number.isFinite(unitPrice) ? unitPrice.toFixed(2) : '0.00',
      },
    };
  }

  if (!variantId) return null;

  return {
    kind: BomLineKind.VARIANT,
    status: BomLineStatus.FOUND,
    sku: item.sku || '',
    quantity,
    variantId,
    productId,
    productHandle,
    title: productTitle,
    vendor: item.vendor || '',
    price: item.final_price ?? item.price ?? null,
    properties: null,
  };
}

/**
 * @typedef {object} BomAddCartButtonRefs
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} [status]
 */

/**
 * Adds every current cart line to the active BoM.
 * @extends {HTMLElement}
 */
class BomAddCartButton extends HTMLElement {
  connectedCallback() {
    this.#button = this.querySelector('[data-bom-add]') || this.querySelector('button');
    this.#status = this.querySelector('[data-bom-status]');
    this.#button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
  }

  /** @type {HTMLButtonElement|null} */
  #button = null;

  /** @type {HTMLElement|null} */
  #status = null;

  #onClick = async (event) => {
    event.preventDefault();
    if (!this.#button || this.#button.disabled) return;

    if (this.#button.dataset.bomState === 'added') {
      openBomDrawer();
      return;
    }

    this.#button.disabled = true;
    this.#setStatus('', false);

    try {
      await bomStore.init();
      if (!bomStore.canEdit) {
        this.#setStatus(this.dataset.textReadOnly || 'This shared BoM is read-only.', true);
        return;
      }

      const lines = await this.#loadCartLines();
      if (!lines.length) {
        this.#setStatus(this.dataset.textEmpty || 'Your cart is empty.', true);
        return;
      }

      let added = 0;
      for (const line of lines) {
        if (bomStore.addLine(line)) added += 1;
      }

      if (!added) {
        this.#setStatus(this.dataset.textError || 'Could not add to BoM.', true);
        return;
      }

      const addedLabel = this.dataset.textAdded || 'Added to BoM';
      this.#setStatus(addedLabel, false);
      markBomButtonAdded(this.#button, addedLabel);
      playBomAddedAnimation(this.#button, this);
    } catch (error) {
      console.warn('[bom-add] cart add failed:', error);
      this.#setStatus(this.dataset.textError || 'Could not add to BoM.', true);
    } finally {
      if (this.#button && this.dataset.cartEmpty !== 'true') {
        this.#button.disabled = false;
      }
    }
  };

  /**
   * @returns {Promise<Array<Partial<import('@theme/bom-store').BomLine>>>}
   */
  async #loadCartLines() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);

    const cart = await response.json();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    return items.map(lineFromCartItem).filter(Boolean);
  }

  #setStatus(message, isError) {
    if (!this.#status) return;
    this.#status.textContent = message;
    this.#status.hidden = !message;
    this.#status.setAttribute('data-tone', isError ? 'error' : 'success');
    // Success on a product card is the button animation. Keep the live text
    // out of the layout so the card does not grow.
    if (this.#status.classList.contains('pa-card-add__bom-status')) {
      this.#status.classList.toggle('visually-hidden', !isError);
    }
  }
}

if (!customElements.get('bom-add-cart-button')) {
  customElements.define('bom-add-cart-button', BomAddCartButton);
}
